// src/styles/theme.js

export const colors = {
  // Brand Colors
  primary: '#00A3E0',
  secondary: '#ee6123',
  
  // Gradients
  gradient: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
  
  // Grays
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
  
  // Status Colors
  success: '#10b981',
  successLight: '#d1fae5',
  successDark: '#065f46',
  
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  warningDark: '#92400e',
  
  error: '#ef4444',
  errorLight: '#fee2e2',
  errorDark: '#991b1b',
  
  info: '#3b82f6',
  infoLight: '#dbeafe',
  infoDark: '#1e40af',
  
  purple: '#8b5cf6',
  purpleLight: '#e9d5ff',
  purpleDark: '#6b21a8',
  
  // Background
  background: '#f8fafc',
  white: '#ffffff'
};

export const spacing = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
  '3xl': '4rem'
};

export const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  '4xl': '2.5rem'
};

export const borderRadius = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px'
};

export const shadows = {
  sm: '0 2px 4px rgba(0,0,0,0.1)',
  md: '0 4px 6px rgba(0,0,0,0.1)',
  lg: '0 8px 25px rgba(0,0,0,0.1)',
  xl: '0 20px 60px rgba(0,0,0,0.3)'
};

export const transitions = {
  fast: '0.15s ease',
  base: '0.2s ease',
  slow: '0.3s ease'
};

// Component Styles
export const styles = {
  // Headers
  header: {
    background: colors.white,
    color: colors.gray900,
    padding: spacing.md,
    boxShadow: shadows.sm,
    borderBottom: `3px solid ${colors.primary}`
  },
  
  headerTitle: {
    fontSize: fontSize['2xl'],
    fontWeight: 'bold',
    margin: '0 0 0.25rem 0',
    color: colors.gray900
  },
  
  headerSubtitle: {
    fontSize: fontSize.sm,
    margin: '0',
    color: colors.gray500
  },
  
  // Cards
  card: {
    backgroundColor: colors.white,
    padding: spacing.lg,
    margin: spacing.md,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.md,
    border: `1px solid ${colors.gray200}`
  },
  
  statsCard: {
    backgroundColor: colors.white,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.sm,
    textAlign: 'center',
    margin: spacing.sm,
    border: `1px solid ${colors.gray200}`
  },
  
  driverCard: {
    backgroundColor: colors.white,
    padding: spacing.xl,
    margin: spacing.md,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.lg,
    border: `1px solid ${colors.gray200}`,
    cursor: 'pointer',
    transition: `all ${transitions.slow}`,
    position: 'relative',
    overflow: 'hidden'
  },
  
  // Buttons
  button: {
    backgroundColor: colors.info,
    color: colors.white,
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    margin: '0.25rem',
    fontSize: fontSize.sm,
    fontWeight: '600',
    transition: `background-color ${transitions.base}`
  },
  
  buttonGreen: {
    background: colors.gradient,
    color: colors.white,
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    margin: '0.25rem',
    fontSize: fontSize.sm,
    fontWeight: '600'
  },
  
  buttonOrange: {
    backgroundColor: colors.warning,
    color: colors.white,
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    margin: '0.25rem',
    fontSize: fontSize.sm,
    fontWeight: '600'
  },
  
  backButton: {
    background: colors.gray100,
    border: `1px solid ${colors.gray300}`,
    color: colors.gray700,
    padding: '0.5rem 1rem',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontSize: fontSize.sm,
    marginBottom: spacing.md,
    fontWeight: '500'
  },
  
  // Forms
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.gray300}`,
    borderRadius: borderRadius.sm,
    fontSize: fontSize.sm,
    marginBottom: spacing.md
  },
  
  select: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.gray300}`,
    borderRadius: borderRadius.sm,
    fontSize: fontSize.sm,
    marginBottom: spacing.md,
    backgroundColor: colors.white
  },
  
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.gray300}`,
    borderRadius: borderRadius.sm,
    fontSize: fontSize.sm,
    marginBottom: spacing.md,
    minHeight: '80px',
    resize: 'vertical'
  },
  
  // Badges
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: borderRadius.full,
    fontSize: fontSize.xs,
    fontWeight: '600',
    margin: '0.125rem'
  },
  
  badgePending: {
    backgroundColor: colors.warningLight,
    color: colors.warningDark,
    border: `1px solid ${colors.warning}`
  },
  
  badgePickedUp: {
    backgroundColor: colors.infoLight,
    color: colors.infoDark,
    border: `1px solid ${colors.info}`
  },
  
  badgeDelivered: {
    backgroundColor: colors.successLight,
    color: colors.successDark,
    border: `1px solid ${colors.success}`
  },
  
  // Login
  loginContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.gradient,
    padding: '20px'
  },
  
  loginBox: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing['3xl'],
    boxShadow: shadows.xl,
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center'
  },
  
  loginTitle: {
    fontSize: fontSize['3xl'],
    fontWeight: 'bold',
    color: colors.gray800,
    marginBottom: spacing.sm
  },
  
  loginSubtitle: {
    color: colors.gray500,
    marginBottom: spacing.xl,
    fontSize: fontSize.base
  },
  
  pinInput: {
    width: '100%',
    padding: spacing.md,
    fontSize: fontSize['2xl'],
    textAlign: 'center',
    border: `2px solid ${colors.gray200}`,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.md,
    letterSpacing: '0.5rem',
    fontWeight: 'bold',
    outline: 'none',
    transition: `border-color ${transitions.base}`
  },
  
  pinError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.error,
    backgroundColor: colors.errorLight,
    padding: '0.75rem',
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    fontSize: fontSize.sm
  },
  
  loginButton: {
    width: '100%',
    padding: spacing.md,
    fontSize: fontSize.lg,
    fontWeight: 'bold',
    color: colors.white,
    background: colors.gradient,
    border: 'none',
    borderRadius: borderRadius.lg,
    cursor: 'pointer',
    transition: `transform ${transitions.base}, box-shadow ${transitions.base}`,
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
  },
  
  // Layout
  centerContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: spacing.md
  },
  
  gridTwoCols: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.md,
    marginBottom: spacing.md
  },
  
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md
  },
  
  // Driver Icon
  driverIcon: {
    width: '64px',
    height: '64px',
    background: colors.gradient,
    borderRadius: borderRadius.full,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem auto',
    boxShadow: `0 4px 12px rgba(0, 163, 224, 0.3)`
  }
};

const theme =  { colors, spacing, fontSize, borderRadius, shadows, transitions, styles };
export default theme;